/**
 * Asks what the chosen field's values mean, where the answer changes what gets stored: the unit of a
 * number, or what makes a yes/no true when the CRM field is a date or a picklist.
 *
 * Nothing is chosen for the advisor. A number read in the wrong unit is wrong by a thousandfold, and
 * a yes/no read by the wrong rule marks the wrong people deceased, so both stay the advisor's call.
 * What this does is put the choice in plain sight, one click away, with a hint about the usual answer
 * where the field's type gives one.
 */
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import type { Prospecting } from '@client/api/types';
import type { FieldMapRow } from '@client/hooks/useCrmFieldMap';
import { SCALE_LABELS, fieldMapCopy } from '../lib/field-map-copy';
import { UNMAPPED } from '../lib/field-map-groups';

const SCALES = Object.keys(SCALE_LABELS) as NonNullable<Prospecting.FieldSemantics['scale']>[];

interface ValueMeaningProps {
  row: FieldMapRow;
  crmName: string;
  disabled: boolean;
  onChange: (semantics: Prospecting.FieldSemantics) => void;
}

export function ValueMeaning({ row, crmName, disabled, onChange }: ValueMeaningProps) {
  if (row.selected === UNMAPPED) return null;

  if (row.kind === 'number') {
    return (
      <div className="mt-3">
        <p className="text-sm text-gray-700">{fieldMapCopy.unit.question}</p>
        <div
          role="group"
          aria-label={fieldMapCopy.unit.label(row.label)}
          className="mt-1.5 flex flex-wrap gap-2"
        >
          {SCALES.map(scale => (
            <Button
              key={scale}
              type="button"
              size="sm"
              variant={row.semantics?.scale === scale ? 'default' : 'outline'}
              aria-pressed={row.semantics?.scale === scale}
              disabled={disabled}
              onClick={() => onChange({ scale })}
            >
              {SCALE_LABELS[scale]}
            </Button>
          ))}
        </div>
        {!row.semantics?.scale && row.selectedField?.type === 'currency' && (
          <p className="mt-1.5 text-xs text-gray-500">{fieldMapCopy.unit.currencyHint(crmName)}</p>
        )}
      </div>
    );
  }

  if (!row.needsBooleanRule) return null;

  const anyValue = Boolean(row.semantics?.presentMeansTrue);
  const oneValue = row.semantics?.truthyValues?.[0];
  const values = row.selectedField?.picklistValues ?? [];
  const isDate = row.selectedField?.type === 'date' || row.selectedField?.type === 'datetime';

  return (
    <div className="mt-3">
      <p className="text-sm text-gray-700">{fieldMapCopy.yesRule.question}</p>
      <div
        role="group"
        aria-label={fieldMapCopy.yesRule.label(row.label)}
        className="mt-1.5 flex flex-wrap items-center gap-2"
      >
        <Button
          type="button"
          size="sm"
          variant={anyValue ? 'default' : 'outline'}
          aria-pressed={anyValue}
          disabled={disabled}
          onClick={() => onChange({ presentMeansTrue: true })}
        >
          {fieldMapCopy.yesRule.anyValue}
        </Button>
        {values.length > 0 && (
          <Select
            value={oneValue}
            onValueChange={value => onChange({ truthyValues: [value] })}
            disabled={disabled}
          >
            <SelectTrigger
              size="sm"
              className="w-56"
              aria-label={fieldMapCopy.yesRule.label(row.label)}
            >
              <SelectValue placeholder={fieldMapCopy.yesRule.pickValue} />
            </SelectTrigger>
            <SelectContent>
              {values.map(value => (
                <SelectItem
                  key={value}
                  value={value}
                >
                  {fieldMapCopy.yesRule.oneValue(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {row.missingYesRule && (
        <p className="mt-1.5 text-xs text-gray-500">
          {fieldMapCopy.yesRule.unread}
          {isDate ? ` ${fieldMapCopy.yesRule.dateHint}` : ''}
        </p>
      )}
    </div>
  );
}
